import { useState } from "react";
import { updatePdc } from "@/lib/api";

const COMPETENZA_OPTIONS = ["AP", "AA", "AS"];
const TIPO_OPTIONS = ["V", "T"];

export default function PdcEditor({ pdc, modalita, causali, gruppiModalita, gruppiSettori, gruppiCausali, onRefresh }) {
  // Track which cell is currently being edited: { conto, field }
  const [editingCell, setEditingCell] = useState(null);
  const [textValue, setTextValue] = useState(""); // buffer for text inputs
  const [popupCodes, setPopupCodes] = useState(null); // {conto, codes} when showing causali popup

  const grModLabel = (id) => {
    const g = gruppiModalita.find((x) => x.codice === id);
    return g ? g.descrizione : "";
  };
  const grSetLabel = (id) => {
    const g = gruppiSettori.find((x) => x.codice === id);
    return g ? g.descrizione : "";
  };
  // Modalità collegate al gruppo (compatte, per visualizzazione in colonna)
  const modalitaDelGruppo = (gruppoId) => {
    if (!gruppoId) return [];
    const g = gruppiModalita.find((x) => x.codice === gruppoId);
    if (!g) return [];
    const codes = [g.mod1, g.mod2, g.mod3, g.mod4, g.mod5].filter((x) => x != null);
    return codes.map((c) => {
      const m = modalita.find((x) => x.codice === c);
      return m ? { codice: c, descrizione: m.descrizione } : { codice: c, descrizione: "" };
    });
  };
  // Causali collegate al gruppo causali
  const causaliDelGruppo = (gruppoId) => {
    if (!gruppoId || !causali) return [];
    const g = gruppiCausali.find((x) => x.codice === gruppoId);
    if (!g) return [];
    return (g.causali_codici || []).map((c) => {
      const cau = causali.find((x) => x.codice === c);
      return cau ? { codice: c, descrizione: cau.descrizione } : { codice: c, descrizione: "" };
    });
  };

  // Settori collegati al gruppo settori
  const settoriDelGruppo = (gruppoId) => {
    if (!gruppoId) return [];
    const g = gruppiSettori.find((x) => x.codice === gruppoId);
    if (!g) return [];
    return [g.set1, g.set2, g.set3, g.set4, g.set5].filter((x) => x);
  };

  const saveField = async (conto, field, value) => {
    try {
      const payload = { [field]: value };
      // When Modo changes, also reset Modalità since it's cascaded
      if (field === "modo_gruppo_id") payload.modalita = null;
      await updatePdc(conto, payload);
      setEditingCell(null);
      await onRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const startEditText = (conto, field, currentValue) => {
    setEditingCell({ conto, field });
    setTextValue(currentValue ?? "");
  };

  const commitText = async (conto, field) => {
    await saveField(conto, field, textValue);
  };

  const cellStyle = { cursor: "cell", minHeight: 20 };
  const activeStyle = { background: "#fffde7" };

  const renderSelectCell = (r, field, currentVal, options, displayFn) => {
    const isEditing = editingCell?.conto === r.conto && editingCell?.field === field;
    if (isEditing) {
      return (
        <td style={activeStyle}>
          <select
            data-testid={`pdc-cell-${field}-${r.conto}`}
            autoFocus
            value={currentVal ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              const parsed = v === "" ? null : (isNaN(Number(v)) ? v : Number(v));
              saveField(r.conto, field, parsed);
            }}
            onBlur={() => setEditingCell(null)}
            style={{ width: "100%", fontSize: 11 }}
          >
            <option value="">—</option>
            {options}
          </select>
        </td>
      );
    }
    return (
      <td style={cellStyle} onClick={() => setEditingCell({ conto: r.conto, field })}>
        {displayFn()}
      </td>
    );
  };

  const renderTextCell = (r, field, currentVal, width) => {
    const isEditing = editingCell?.conto === r.conto && editingCell?.field === field;
    if (isEditing) {
      return (
        <td style={activeStyle}>
          <input
            data-testid={`pdc-cell-${field}-${r.conto}`}
            autoFocus
            type="text"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onBlur={() => commitText(r.conto, field)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitText(r.conto, field);
              if (e.key === "Escape") setEditingCell(null);
            }}
            style={{ width: width || "100%", fontSize: 11 }}
          />
        </td>
      );
    }
    return (
      <td style={cellStyle} onClick={() => startEditText(r.conto, field, currentVal)}>
        {currentVal ?? ""}
      </td>
    );
  };

  return (
    <>
      <div style={{ padding: "8px 12px", background: "#f5f5f5", borderBottom: "1px solid #ccc", fontSize: 12, color: "#666" }}>
        Schema Piano dei Conti (PDC) — {pdc.length} voci. Clicca su una cella per modificarla.
      </div>
      <div style={{ maxHeight: "70vh", overflow: "auto" }}>
        <table className="sw-table" style={{ fontSize: 11, textTransform: "uppercase", tableLayout: "fixed" }}>
          <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 1 }}>
            <tr>
              <th style={{ width: 70 }}>Conto</th>
              <th>Descrizione</th>
              <th style={{ width: 110 }}>Plesso</th>
              <th style={{ width: 90 }}>Settori</th>
              <th style={{ width: 80 }}>Anno</th>
              <th style={{ width: 120 }}>Modo</th>
              <th style={{ width: 130 }}>Modalità</th>
              <th style={{ width: 110 }}>Causale 1</th>
              <th style={{ width: 110 }}>Causale 2</th>
              <th style={{ width: 140 }}>Causale 3</th>
              <th style={{ width: 55 }}>Cella</th>
              <th style={{ width: 45 }}>Tipo</th>
              <th style={{ width: 60, textAlign: "center" }}>Non St.</th>
            </tr>
          </thead>
          <tbody>
            {pdc.length === 0 ? (
              <tr><td colSpan={13} className="sw-empty"><p>Nessuna voce PDC</p></td></tr>
            ) : pdc.map((r) => {
              return (
                <tr key={r.conto} data-testid={`pdc-row-${r.conto}`}>
                  <td className="center-col" style={{ fontWeight: 700, fontFamily: "Consolas" }}>{r.conto}</td>

                  {renderTextCell(r, "descrizione", r.descrizione)}

                  {/* Plesso: click per cambiare gruppo, display mostra i settori collegati */}
                  {(() => {
                    const field = "plesso_gruppo_id";
                    const isEditing = editingCell?.conto === r.conto && editingCell?.field === field;
                    if (isEditing) {
                      return (
                        <td key="plesso" style={activeStyle}>
                          <select
                            data-testid={`pdc-cell-plesso_gruppo_id-${r.conto}`}
                            autoFocus
                            value={r.plesso_gruppo_id ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              saveField(r.conto, field, v === "" ? null : Number(v));
                            }}
                            onBlur={() => setEditingCell(null)}
                            style={{ width: "100%", fontSize: 11 }}
                          >
                            <option value="">—</option>
                            {gruppiSettori.map((g) => <option key={g.codice} value={g.codice}>{g.descrizione}</option>)}
                          </select>
                        </td>
                      );
                    }
                    const settoriList = settoriDelGruppo(r.plesso_gruppo_id);
                    return (
                      <td key="plesso" style={{ ...cellStyle, fontSize: 11 }} onClick={() => setEditingCell({ conto: r.conto, field })}>
                        {r.plesso_gruppo_id && grSetLabel(r.plesso_gruppo_id) ? (
                          <span style={{ fontWeight: 700 }}>{grSetLabel(r.plesso_gruppo_id)}</span>
                        ) : settoriList.length === 0 ? (
                          <span style={{ color: "#888" }}>{r.plesso_testo || r.plesso || "—"}</span>
                        ) : null}
                      </td>
                    );
                  })()}

                  {/* SETTORI: codici dei settori del gruppo scelto in Plesso (sola lettura, compatto) */}
                  <td style={{ fontSize: 10, lineHeight: 1.3, maxWidth: 110 }}>
                    {(() => {
                      const settoriList = settoriDelGruppo(r.plesso_gruppo_id);
                      if (settoriList.length === 0) return <span style={{ color: "#bbb" }}>—</span>;
                      return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                          {settoriList.map((s) => (
                            <span key={s} style={{ background: "#e8f5e9", color: "#1b5e20", padding: "1px 5px", borderRadius: 3, fontWeight: 600, whiteSpace: "nowrap" }}>
                              {s}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>

                  {renderSelectCell(r, "competenza", r.competenza,
                    COMPETENZA_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>),
                    () => r.competenza || ""
                  )}

                  {renderSelectCell(r, "modo_gruppo_id", r.modo_gruppo_id,
                    gruppiModalita.map((g) => <option key={g.codice} value={g.codice}>{g.descrizione}</option>),
                    () => r.modo_gruppo_id ? grModLabel(r.modo_gruppo_id) : (r.modo || "")
                  )}

                  {/* Modalità: mostra SOLO i codici delle modalità collegate al gruppo (sola lettura) */}
                  <td style={{ fontSize: 10, lineHeight: 1.3, maxWidth: 140, overflow: "hidden" }}>
                    {(() => {
                      const modList = modalitaDelGruppo(r.modo_gruppo_id);
                      if (!r.modo_gruppo_id || modList.length === 0) return <span style={{ color: "#bbb" }}>—</span>;
                      return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                          {modList.map((m) => (
                            <span key={m.codice} style={{ background: "#e3f2fd", color: "#0d47a1", padding: "1px 5px", borderRadius: 3, fontWeight: 600, whiteSpace: "nowrap" }}>
                              {m.codice}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>

                  {/* Causale 1/2: gruppo editabile */}
                  {[1, 2].map((n) => {
                    const field = `causale${n}_gruppo_id`;
                    const isEditing = editingCell?.conto === r.conto && editingCell?.field === field;
                    if (isEditing) {
                      return (
                        <td key={n} style={activeStyle}>
                          <select
                            data-testid={`pdc-cell-${field}-${r.conto}`}
                            autoFocus
                            value={r[field] ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              saveField(r.conto, field, v === "" ? null : Number(v));
                            }}
                            onBlur={() => setEditingCell(null)}
                            style={{ width: "100%", fontSize: 11 }}
                          >
                            <option value="">—</option>
                            {gruppiCausali.map((g) => (
                              <option key={g.codice} value={g.codice}>{g.codice} - {g.descrizione}</option>
                            ))}
                          </select>
                        </td>
                      );
                    }
                    const gid = r[field];
                    const g = gruppiCausali.find((x) => x.codice === gid);
                    return (
                      <td key={n} style={{ ...cellStyle, fontSize: 10, lineHeight: 1.3 }} onClick={() => setEditingCell({ conto: r.conto, field })}>
                        {gid == null || !g ? (
                          <span style={{ color: "#bbb" }}>—</span>
                        ) : (
                          <span style={{ background: "#fff3e0", color: "#bf360c", padding: "1px 5px", borderRadius: 3, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {g.codice} - {g.descrizione}
                          </span>
                        )}
                      </td>
                    );
                  })}

                  {/* CAUSALE 3: sola lettura, codici delle causali collegate ai gruppi in C1 + C2 (compatto) */}
                  <td style={{ fontSize: 10, lineHeight: 1.3, maxWidth: 160 }}>
                    {(() => {
                      const setCodici = new Set();
                      [r.causale1_gruppo_id, r.causale2_gruppo_id].forEach((gid) => {
                        if (gid == null) return;
                        const g = gruppiCausali.find((x) => x.codice === gid);
                        if (g && g.causali_codici) g.causali_codici.forEach((c) => setCodici.add(c));
                      });
                      const codes = Array.from(setCodici).sort((a, b) => a - b);
                      if (codes.length === 0) return <span style={{ color: "#bbb" }}>—</span>;
                      if (codes.length > 5) {
                        return (
                          <span
                            data-testid={`pdc-cau3-more-${r.conto}`}
                            onClick={(e) => { e.stopPropagation(); setPopupCodes({ conto: r.conto, codes }); }}
                            style={{ cursor: "pointer", color: "#0066cc", fontWeight: 700, textDecoration: "underline", textTransform: "none" }}
                          >
                            ...più di 5 ({codes.length})
                          </span>
                        );
                      }
                      return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                          {codes.map((c) => (
                            <span key={c} style={{ background: "#fff3e0", color: "#bf360c", padding: "1px 5px", borderRadius: 3, fontWeight: 600, whiteSpace: "nowrap" }}>
                              {c}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>

                  {renderTextCell(r, "cella", r.cella)}

                  {renderSelectCell(r, "tipo", r.tipo,
                    TIPO_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>),
                    () => r.tipo || ""
                  )}

                  <td className="center-col" style={cellStyle}>
                    <input
                      data-testid={`pdc-cell-non_stampa-${r.conto}`}
                      type="checkbox"
                      checked={!!r.non_stampa}
                      onChange={(e) => saveField(r.conto, "non_stampa", e.target.checked)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Popup codici causali */}
      {popupCodes && (
        <div
          data-testid="pdc-cau3-popup"
          onClick={() => setPopupCodes(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", padding: 20, borderRadius: 6, maxWidth: 520, width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Causali collegate — Conto {popupCodes.conto}</h3>
              <button onClick={() => setPopupCodes(null)} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#888" }}>×</button>
            </div>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>{popupCodes.codes.length} codici causale uniti dai gruppi Causale 1 e Causale 2:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: "60vh", overflow: "auto" }}>
              {popupCodes.codes.map((c) => {
                const cau = (causali || []).find((x) => x.codice === c);
                return (
                  <span key={c} style={{ background: "#fff3e0", color: "#bf360c", padding: "3px 8px", borderRadius: 3, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>
                    <b>{c}</b>{cau ? ` - ${cau.descrizione}` : ""}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
